import { ApiError, unwrap, type Schemas } from '@mrigl/api-client';
import {
  LOCALES,
  parseMajor,
  pickLocalized,
  toMajorString,
  money,
  type Locale,
} from '@mrigl/shared';
import { useEffect, useState } from 'react';
import { api, errorMessage } from '../../api.js';
import type { I18n } from '../../i18n.js';

export type StoreDetails = Schemas['StoreDetails'];
export type Localized = Partial<Record<Locale, string>>;

/** Store languages in the platform's canonical order. */
export function storeLocales(store: StoreDetails): Locale[] {
  return LOCALES.filter((locale) => store.enabledLocales.includes(locale));
}

/** Text for the admin's language, falling back to the store's main language. */
export function display(text: Localized, locale: Locale, store: StoreDetails, empty = ''): string {
  return pickLocalized(text, locale, store.defaultLocale) ?? Object.values(text)[0] ?? empty;
}

/** Drops empty and whitespace-only translations. */
export function cleanText(text: Localized): Localized {
  return Object.fromEntries(
    Object.entries(text)
      .map(([locale, value]) => [locale, value.trim()])
      .filter(([, value]) => value !== ''),
  ) as Localized;
}

/** "25,5" or "25.500" (dinars) → "25500" (millimes); null when not a valid amount. */
export function toMinor(text: string): string | null {
  const normalized = text.trim().replace(',', '.');
  if (normalized === '') return null;
  try {
    const amount = parseMajor(normalized, 'TND');
    return amount.minor < 0n ? null : amount.minor.toString();
  } catch {
    return null;
  }
}

/** "25500" (millimes) → "25.500" for editing. */
export function toMajor(minor: string): string {
  return toMajorString(money(BigInt(minor), 'TND'));
}

export function moveItem<T>(items: readonly T[], index: number, delta: -1 | 1): T[] {
  const target = index + delta;
  if (target < 0 || target >= items.length) return [...items];
  const next = [...items];
  const [item] = next.splice(index, 1);
  if (item !== undefined) next.splice(target, 0, item);
  return next;
}

/** Random option-value key (stable once created; the API only needs uniqueness). */
export function newKey(): string {
  return `v${Math.random().toString(36).slice(2, 8)}`;
}

/** Problem codes of the catalog API mapped to the editor's messages. */
export function catalogError(t: I18n['t'], failure: unknown): string {
  if (failure instanceof ApiError) {
    switch (failure.code) {
      case 'version-conflict':
        return t('admin.product.error.version-conflict');
      case 'handle-taken':
        return t('admin.product.error.handle-taken');
      case 'sku-taken':
        return t('admin.product.error.sku-taken');
      case 'validation-failed':
        return t('admin.product.error.validation');
      default:
        break;
    }
  }
  return errorMessage(t, failure);
}

/** Loads the store (names, languages, the member's permissions). */
export function useStore(storeId: string, t: I18n['t']) {
  const [store, setStore] = useState<StoreDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    void unwrap(api.GET('/admin-api/v1/stores/{storeId}', { params: { path: { storeId } } }))
      .then(setStore)
      .catch((failure: unknown) => {
        setError(errorMessage(t, failure));
      });
  }, [storeId, t]);
  return { store, error };
}
